/**
 * Policies - what a backup is expected to do.
 *
 * WHY THIS PAGE EXISTS. ShadowControl publishes each job's schedule, so its
 * machines can be judged against what they were actually asked to do. Datto,
 * MSP360 and Kaseya publish nothing of the kind - so everything from them was
 * judged by a flat "warn at 26 hours", which quietly assumes every machine on
 * earth backs up nightly. Half-hourly machines looked fine when they had been
 * down for a day; weekly ones were permanently amber.
 *
 * THE ORDER IS THE WHOLE FEATURE, so the page states it plainly rather than
 * leaving people to discover it: a device overrides its client, a client
 * overrides the default, and anything a machine actually demonstrates beats
 * the default too.
 *
 * WHAT A POLICY CAN SAY, and why each field is here rather than being one more
 * box nobody fills in. Modelled on Backup Radar's Backup Plan wizard, which
 * Damon walked me through on 2026-08-09 because the first pass was too
 * stringent - but only the parts that change an answer:
 *
 *   interval    how often. The one field that was already here.
 *   run days    the Monday-morning problem. A weekday-only server has been
 *               quiet 60 hours by Monday and reads as failed.
 *   window      GrantStreet-BCDR. Warning at 29.5h on a nightly job whose
 *               window has not even closed yet is a warning about nothing.
 *   RPO         the escape hatch - "nightly, but I do not care until 36 hours".
 *   grace       now DERIVED from the interval by default, so nobody has to
 *               reason about it. A quarter of the cadence, floor of two hours.
 *
 * The preview under the form is not decoration. Every field here changes two
 * numbers - when this warns and when it fails - and a settings page that hides
 * its own consequences is one people set once and never trust again.
 */

import { useEffect, useMemo, useState } from 'react';
import { DurationField } from './components/DurationField';
import { TimeField } from './components/TimeField';
import { minutesToHhmm } from './components/timeMath';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import { errText } from './btApi';

function call<TIn extends object, TOut>(name: string) {
  return async (data: TIn): Promise<TOut> => (await httpsCallable<TIn, TOut>(functions, name)(data)).data;
}

export interface SchedulePolicy {
  everyHours: number | null;
  days?: number[];
  graceHours?: number | null;
  windowStart?: number | null;
  windowEnd?: number | null;
  rpoHours?: number | null;
  rpoByDay?: Record<string, number> | null;
  warningsAreSuccess?: boolean;
  missesBeforeFail?: number | null;
  /** Judged on the vendor result alone - never on how long ago it ran. */
  manual?: boolean;
  note?: string;
}

interface ClientRow {
  id: string;
  name: string;
  policy: SchedulePolicy | null;
  psaGrouping?: 'device' | 'client' | null;
  ticketProfileId?: string | null;
  reportCadence?: 'off' | 'weekly' | 'monthly' | null;
}

const getPolicies = call<{ orgId: string },
  {
    global: SchedulePolicy | null; builtInHours: number; clients: ClientRow[];
    timeZone: string | null; locale: string | null;
  }
>('getPolicies');
const setOrgTimeZone =
  call<
    { orgId: string; timeZone: string | null; locale?: string | null },
    { ok: boolean; timeZone: string | null }
  >('setOrgTimeZone');

/*
 * Kept in step with functions/src/format.ts by hand, which is a duplication
 * worth naming: the server VALIDATES against its own list, so a mismatch here
 * is rejected rather than silently stored. Six entries that change about never.
 */
const LOCALES: ReadonlyArray<{ id: string; label: string; example: string }> = [
  { id: 'en-US', label: 'United States', example: 'August 12, 2026, 9:15 PM' },
  { id: 'en-GB', label: 'United Kingdom', example: '12 August 2026, 21:15' },
  { id: 'en-CA', label: 'Canada', example: 'August 12, 2026, 9:15 p.m.' },
  { id: 'en-AU', label: 'Australia', example: '12 August 2026, 9:15 pm' },
  { id: 'en-IE', label: 'Ireland', example: '12 August 2026, 21:15' },
  { id: 'en-NZ', label: 'New Zealand', example: '12 August 2026, 9:15 pm' },
];
const setGlobalPolicy = call<{ orgId: string; policy: SchedulePolicy | null }, { ok: boolean }>('setGlobalPolicy');
const setClientPolicy = call<{ orgId: string; clientId: string; policy: SchedulePolicy | null }, { ok: boolean }>('setClientPolicy');
const setDevicePolicy = call<{ orgId: string; targetId: string; policy: SchedulePolicy | null }, { ok: boolean }>('setDevicePolicy');
const setClientTicketGrouping =
  call<{ orgId: string; clientId: string; grouping: 'device' | 'client' | null }, { ok: boolean }>('setClientTicketGrouping');
const listTicketProfiles =
  call<{ orgId: string }, { profiles: Array<{ id: string; name: string }> }>('listTicketProfiles');
const setClientTicketProfile =
  call<{ orgId: string; clientId: string; profileId: string | null }, { ok: boolean }>('setClientTicketProfile');
const setClientReportCadence =
  call<{ orgId: string; clientId: string; cadence: 'off' | 'weekly' | 'monthly' }, { ok: boolean }>('setClientReportCadence');
const sendClientReport =
  call<{ orgId: string; clientId: string; days?: number },
       { ok: boolean; machines: number; needAttention: number }>('sendClientReport');

/**
 * Intervals people actually mean, rather than a free-text hours box.
 *
 * A number field invites "0.75" and typos that fail a hundred machines; these
 * are the cadences backup software is actually configured with.
 */
const PRESETS: { label: string; hours: number }[] = [
  { label: 'Every 15 minutes', hours: 0.25 },
  { label: 'Every 30 minutes', hours: 0.5 },
  { label: 'Hourly', hours: 1 },
  { label: 'Every 4 hours', hours: 4 },
  { label: 'Every 12 hours', hours: 12 },
  { label: 'Daily', hours: 24 },
  { label: 'Every 2 days', hours: 48 },
  { label: 'Weekly', hours: 168 },
];


const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEKDAYS = [1, 2, 3, 4, 5];
const ALL_DAYS = [0, 1, 2, 3, 4, 5, 6];

// ---------------------------------------------------------------------------
// The same arithmetic the server does, so the preview cannot disagree with the
// verdict. Duplicated on purpose and deliberately tiny: the alternative is a
// round trip on every keystroke, and a form that cannot show its own effect
// until you save it is a form people save wrong.
// ---------------------------------------------------------------------------

const BUILT_IN_HOURS = 24;

function defaultGraceHours(everyHours: number | null | undefined): number {
  const every = everyHours && everyHours > 0 ? everyHours : BUILT_IN_HOURS;
  return Math.max(2, every * 0.25);
}

function thresholdsFor(p: SchedulePolicy): { warnAt: number; failAt: number } {
  const every = p.everyHours && p.everyHours > 0 ? p.everyHours : BUILT_IN_HOURS;
  const grace = p.graceHours ?? defaultGraceHours(every);
  const misses = Math.max(2, Math.round(p.missesBeforeFail ?? 2));
  const warnAt = p.rpoHours && p.rpoHours > 0 ? p.rpoHours : Math.max(every + grace, 2);
  const failAt = Math.max(warnAt + every, every * misses + grace, 6);
  return { warnAt, failAt };
}

function describe(hours: number | null | undefined): string {
  if (!hours || hours <= 0) return 'not set';
  const p = PRESETS.find((x) => Math.abs(x.hours - hours) < 0.01);
  if (p) return p.label;
  if (hours < 1.5) return `Every ${Math.round(hours * 60)} minutes`;
  if (hours < 36) return `Every ${Math.round(hours)} hours`;
  return `Every ${Math.round(hours / 24)} days`;
}

/**
 * A THRESHOLD in words.
 *
 * Deliberately keeps hours up to three days, where describeAge switches to days
 * at 48. An age is being compared against a column that also reads in days, so
 * days are right there; a threshold is a number somebody has to agree to, and
 * rendering a 54-hour failure point as "2 days" invites them to agree to 48.
 */
function hours(h: number): string {
  if (h < 1.5) return `${Math.round(h * 60)} minutes`;
  if (h < 72) return `${Math.round(h * 10) / 10} hours`;
  return `${Math.round(h / 24)} days`;
}

function dayNames(days: number[] | undefined): string {
  if (!days || days.length === 0 || days.length === 7) return 'every day';
  if (days.length === 5 && WEEKDAYS.every((d) => days.includes(d))) return 'Mon-Fri';
  return days.slice().sort((a, b) => a - b).map((d) => DAY_LABELS[d]).join(', ');
}

// The parser that used to sit here went with the text input it existed for.
// The formatter stays - the one-line policy summary still needs it - but it
// now comes from timeMath, so there is one tested implementation rather than
// a copy on each page that uses a time.
const hhmm = minutesToHhmm;

/** One line summarising a whole policy, for the client table. */
export function summarise(p: SchedulePolicy | null): string {
  if (!p) return 'follows the default';
  // Said first and on its own: nothing else in the policy applies, so listing
  // a window or an RPO beside it would describe rules that are not running.
  if (p.manual === true) return 'manual - judged on the vendor result only';
  const bits: string[] = [];
  if (p.everyHours) bits.push(describe(p.everyHours).toLowerCase());
  if (p.days && p.days.length && p.days.length < 7) bits.push(dayNames(p.days));
  if (p.windowStart != null && p.windowEnd != null) {
    bits.push(`${hhmm(p.windowStart)}-${hhmm(p.windowEnd)}${p.windowEnd < p.windowStart ? ' next day' : ''}`);
  }
  if (p.rpoHours) bits.push(`RPO ${hours(p.rpoHours)}`);
  return bits.length ? bits.join(' | ') : 'follows the default';
}

/**
 * Does this policy say nothing at all?
 *
 * MANUAL SAYS SOMETHING, and it is the only kind of policy that says it by
 * leaving every other field empty. Without the first clause this returns true
 * for a manual policy, and the Save button below does
 * `onSave(empty ? null : p)` - so pressing Save DELETED the policy instead of
 * storing it, and the machine went back to following its client. "It says it
 * is manual and also that it follows the client, so it is doing nothing" was
 * exactly right.
 *
 * The server had the identical predicate in usable() and it was fixed there
 * when manual shipped. This copy was not, which is the real lesson: two
 * implementations of one question, in two languages, and only one of them
 * learned the new answer.
 */
export const isEmpty = (p: SchedulePolicy): boolean =>
  p.manual !== true
  && !p.everyHours && !(p.days && p.days.length) && p.windowStart == null && !p.rpoHours;

// ---------------------------------------------------------------------------
// The editor
// ---------------------------------------------------------------------------

function PolicyEditor({ value, onSave, disabled, noneLabel, busy }: {
  value: SchedulePolicy | null;
  onSave: (p: SchedulePolicy | null) => void;
  disabled?: boolean;
  /** What "no policy here" means at this level. */
  noneLabel: string;
  busy?: boolean;
}) {
  const [p, setP] = useState<SchedulePolicy>(() => value ?? { everyHours: null });
  const [advanced, setAdvanced] = useState(
    Boolean(value && (value.windowStart != null || value.rpoHours || value.graceHours || value.warningsAreSuccess)),
  );
  useEffect(() => {
    setP(value ?? { everyHours: null });
  }, [value]);

  const set = (patch: Partial<SchedulePolicy>) => setP((cur) => ({ ...cur, ...patch }));
  const empty = isEmpty(p);
  const t = useMemo(() => thresholdsFor(p), [p]);
  const days = p.days ?? [];

  const toggleDay = (d: number) => {
    const has = days.includes(d);
    const next = has ? days.filter((x) => x !== d) : [...days, d].sort((a, b) => a - b);
    // Seven of seven is the same statement as "every day", and storing it as a
    // list makes every downstream comparison work harder for no gain.
    set({ days: next.length === 7 ? [] : next });
  };

  return (
    <div className="policy-editor">
      {/* FIRST, and deliberately. Everything below stops applying the moment
          this is on, so a reader who meets it last has already spent effort on
          fields that will be ignored. */}
      <label className="check-row" style={{ marginBottom: 10 }}>
        <input
          type="checkbox"
          disabled={disabled}
          checked={p.manual === true}
          onChange={(e) => set({ manual: e.target.checked })}
        />
        <span>
          <strong>Manual</strong> — never judge this on time
          <span className="field-hint" style={{ display: 'block', marginTop: 2 }}>
            For machines nobody can honestly put a schedule on. It goes red only when the vendor
            says the backup failed, and is never late, because nothing here claims to know when it
            should run. A failure still alerts and still raises a ticket — what this removes is our
            opinion about timing, not the monitoring.
          </span>
        </span>
      </label>

      {p.manual === true && (
        <div className="banner" style={{ marginBottom: 10 }}>
          The settings below are not applied while Manual is on. They are kept as they are, so
          switching it off restores the schedule rather than losing it.
        </div>
      )}

      <div className="row-form">
        <label className="field">
          <span className="field-label">Expect a backup</span>
          <select
            className="field-input" style={{ maxWidth: 220 }} disabled={disabled}
            value={p.everyHours ?? ''}
            onChange={(e) => set({ everyHours: e.target.value === '' ? null : Number(e.target.value) })}
          >
            <option value="">{noneLabel}</option>
            {PRESETS.map((x) => <option key={x.hours} value={x.hours}>{x.label}</option>)}
          </select>
        </label>

        <div className="field" style={{ flex: '1 1 320px' }}>
          <span className="field-label">On these days</span>
          <div className="day-chips">
            {ALL_DAYS.map((d) => (
              <button
                key={d} type="button" disabled={disabled}
                className={'day-chip' + (days.length === 0 || days.includes(d) ? ' on' : '')}
                onClick={() => toggleDay(d)}
                title={days.length === 0 ? 'Every day' : undefined}
              >
                {DAY_LABELS[d]}
              </button>
            ))}
            <button type="button" className="btn-link" disabled={disabled}
                    onClick={() => set({ days: [] })}>Every day</button>
            <button type="button" className="btn-link" disabled={disabled}
                    onClick={() => set({ days: WEEKDAYS })}>Mon-Fri</button>
          </div>
          <span className="field-hint">
            Days a backup is expected at all. A machine that was not supposed to run cannot be late -
            this is what stops Monday morning filling with weekend false failures.
          </span>
        </div>
      </div>

      {advanced && (
        <>
          <div className="row-form">
            {/*
              CLICK THE TIME, GET A CLOCK - and get rid of it once it is set.

              The browser's own type="time" was a real improvement on the text
              box it replaced (which could not be typed into at all), but it is
              still a pair of spinners whose picker Chrome draws and Safari does
              not. Damon asked for the clock face we built for the RPO, popped
              up on click and dismissed once the minutes are in.

              A window opening and closing is the ONE thing a clock face is
              actually right for: 2am and 6am are positions on a dial. Which is
              exactly why the RPO below is NOT a clock - a duration has no
              position on one.
            */}
            <label className="field">
              <span className="field-label">Backup window opens</span>
              <TimeField
                label="Backup window opens"
                value={p.windowStart ?? null}
                disabled={disabled}
                allowClear
                onChange={(v) => set({ windowStart: v })}
              />
            </label>
            <label className="field">
              <span className="field-label">and closes</span>
              <TimeField
                label="Backup window closes"
                value={p.windowEnd ?? null}
                disabled={disabled}
                allowClear
                onChange={(v) => set({ windowEnd: v })}
              />
            </label>
            <div className="field" style={{ flex: '1 1 260px' }}>
              <span className="field-label">&nbsp;</span>
              <span className="field-hint">
                Nothing is judged late until the window closes. A closing time EARLIER than the
                opening time means the window runs into the next day, which is the normal case for
                an overnight backup.
              </span>
            </div>
          </div>

          <div className="row-form">
            {/*
              NOT A CLOCK. This was built as one first, and that was wrong.

              A clock face answers "when". An RPO answers "how long", and the
              two only look alike because they share the word "hours". On a
              face, 4 hours and 16 hours are the same place, 13 has to be
              smuggled onto an inner ring, and "two days" cannot be said at
              all - the dial ended up carrying a separate days box beside it
              to cover for what it could not express, which is the tell.

              Three lists say all of it plainly, and are faster for somebody
              who already knows the number off the contract. Behind a popover,
              because this is set once and thereafter only read.
            */}
            <label className="field">
              <span className="field-label">Recovery point objective</span>
              <DurationField
                label="Recovery point objective"
                value={p.rpoHours ?? null}
                disabled={disabled}
                onChange={(hours) => set({ rpoHours: hours })}
              />
              <span className="field-hint">
                The longest gap between successful backups this customer will accept. Leave it
                unset to derive the threshold from the interval above.
              </span>
            </label>
            <label className="field">
              <span className="field-label">Allow this much slack</span>
              <span className="input-unit">
                <input
                  className="field-input" style={{ maxWidth: 90 }} disabled={disabled || Boolean(p.rpoHours)}
                  type="number" min={0} step={0.5}
                  placeholder={String(Math.round(defaultGraceHours(p.everyHours) * 10) / 10)}
                  value={p.graceHours ?? ''}
                  onChange={(e) => set({ graceHours: e.target.value === '' ? null : Number(e.target.value) })}
                />
                <span className="unit">hours</span>
              </span>
            </label>
            {/* The unit sits BESIDE the box, not under it. As a field-hint it
                wrapped onto its own line and read as a caption for the whole
                row - "Failure after / [2] / missed runs" split across three
                lines is not a sentence anybody can parse at a glance. */}
            <label className="field">
              <span className="field-label">Failure after</span>
              <span className="input-unit">
                <input
                  className="field-input" style={{ maxWidth: 80 }} disabled={disabled}
                  type="number" min={2} step={1} placeholder="2"
                  value={p.missesBeforeFail ?? ''}
                  onChange={(e) => set({ missesBeforeFail: e.target.value === '' ? null : Number(e.target.value) })}
                />
                <span className="unit">missed runs</span>
              </span>
            </label>
          </div>

          <div className="row-form">
            <label className="check-row">
              <input
                type="checkbox" disabled={disabled}
                checked={p.warningsAreSuccess === true}
                onChange={(e) => set({ warningsAreSuccess: e.target.checked })}
              />
              <span>
                Treat a vendor warning as a successful backup
                <span className="field-hint">
                  Some products warn about things that are not a backup problem - a skipped open
                  file, a nearly-full target. Only tick this where you have decided that is true
                  for these machines, because it turns a real signal off.
                </span>
              </span>
            </label>
          </div>
        </>
      )}

      <div className="policy-preview">
        <strong>What this means.</strong>{' '}
        {empty ? (
          <span className="dim">Nothing set - {noneLabel}.</span>
        ) : (
          <>
            {p.manual === true
              ? 'Judged on the vendor result only. Never late, and never chased about timing.'
              : <>Expect a backup {describe(p.everyHours).toLowerCase()} on {dayNames(p.days)}</>}
            {p.windowStart != null && p.windowEnd != null
              ? `, between ${hhmm(p.windowStart)} and ${hhmm(p.windowEnd)}${p.windowEnd < p.windowStart ? ' the next day' : ''}`
              : ''}
            . Warning after <strong>{hours(t.warnAt)}</strong> without one, failure after{' '}
            <strong>{hours(t.failAt)}</strong>.
            {p.rpoHours ? ' The recovery point objective sets the warning point directly.' : ''}
          </>
        )}
      </div>

      <div className="row-form" style={{ marginTop: 6 }}>
        <button className="btn" disabled={disabled || busy} onClick={() => onSave(empty ? null : p)}>
          {busy ? 'Saving...' : 'Save'}
        </button>
        {!empty && (
          <button className="btn-link" disabled={disabled || busy} onClick={() => { setP({ everyHours: null }); onSave(null); }}>
            Clear
          </button>
        )}
        <button className="btn-link" type="button" onClick={() => setAdvanced((a) => !a)}>
          {advanced ? 'Hide window, RPO and tolerances' : 'Window, RPO and tolerances'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * The zone every schedule is read in.
 *
 * A picker, never a text box. The server only accepts canonical IANA names, so
 * free text would mostly produce rejections - and the one thing worth being
 * strict about here is that "EST" is a fixed-offset zone that never springs
 * forward, which nobody would guess from typing it.
 */
function TimeZonePanel({ value, locale, onSave, disabled, busy }: {
  value: string | null;
  locale: string | null;
  onSave: (zone: string | null, locale: string | null) => void;
  disabled: boolean;
  busy: boolean;
}) {
  const zones = useMemo<string[]>(() => {
    const f = (Intl as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf;
    const list = typeof f === 'function' ? f.call(Intl, 'timeZone') : [];
    return ['UTC', ...list.filter((z: string) => z !== 'UTC')];
  }, []);

  const browser = useMemo(() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || null; } catch { return null; }
  }, []);

  const [draft, setDraft] = useState<string>(value ?? '');
  useEffect(() => { setDraft(value ?? ''); }, [value]);
  const [loc, setLoc] = useState<string>(locale ?? '');
  useEffect(() => { setLoc(locale ?? ''); }, [locale]);

  // Show the person what the setting MEANS right now, not just its name.
  const nowThere = (() => {
    if (!draft) return null;
    try {
      return new Intl.DateTimeFormat(undefined, {
        timeZone: draft, weekday: 'short', hour: 'numeric', minute: '2-digit',
      }).format(new Date());
    } catch { return null; }
  })();

  const dirty = (value ?? '') !== draft || (locale ?? '') !== loc;

  return (
    <section className="panel">
      <h2 className="panel-title">Time zone</h2>
      <p className="panel-note" style={{ marginTop: 0 }}>
        Run days and overnight windows are read in this zone. Without one, a policy of
        &ldquo;Mon&ndash;Fri, 8pm&ndash;6am&rdquo; is judged in UTC, which can put a 2am backup on the
        wrong day entirely. Daylight saving is handled for you.
      </p>

      <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <select
          value={draft}
          disabled={disabled || busy}
          onChange={(e) => setDraft(e.target.value)}
          style={{ minWidth: 260 }}
        >
          <option value="">No zone - judge on hours elapsed</option>
          {zones.map((z) => <option key={z} value={z}>{z.replace(/_/g, ' ')}</option>)}
        </select>

        {nowThere && <span className="muted">It is {nowThere} there.</span>}
      </div>

      <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 10 }}>
        <label className="field" style={{ minWidth: 260 }}>
          <span className="field-label">Date format</span>
          <select className="field-input" value={loc} disabled={disabled || busy}
                  onChange={(e) => setLoc(e.target.value)}>
            <option value="">Default (United States)</option>
            {LOCALES.map((l) => (
              <option key={l.id} value={l.id}>{l.label} &mdash; {l.example}</option>
            ))}
          </select>
        </label>

        {!disabled && dirty && (
          <button
            className="btn btn-primary"
            disabled={busy}
            onClick={() => onSave(draft === '' ? null : draft, loc === '' ? null : loc)}
          >
            {busy ? 'Saving...' : 'Save'}
          </button>
        )}
        {!disabled && dirty && (
          <button className="btn" disabled={busy}
                  onClick={() => { setDraft(value ?? ''); setLoc(locale ?? ''); }}>Cancel</button>
        )}
      </div>

      <p className="panel-note">
        The date format applies to client reports, alert emails and PSA tickets &mdash; anything
        written by the server. Screens here already follow each person&rsquo;s own browser.
      </p>

      {!value && browser && draft === '' && !disabled && (
        <p className="panel-note" style={{ marginBottom: 0 }}>
          This browser is in <strong>{browser.replace(/_/g, ' ')}</strong>.{' '}
          <button className="btn-link" onClick={() => setDraft(browser)}>Use that</button>
        </p>
      )}
    </section>
  );
}

export function Policies({ orgId, canManage }: { orgId: string | null; canManage: boolean }) {
  const [global, setGlobal] = useState<SchedulePolicy | null>(null);
  const [clients, setClients] = useState<ClientRow[]>([]);
  const [builtIn, setBuiltIn] = useState(BUILT_IN_HOURS);
  const [timeZone, setTimeZone] = useState<string | null>(null);
  const [locale, setLocale] = useState<string | null>(null);
  const [profiles, setProfiles] = useState<Array<{ id: string; name: string }>>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<string | null>(null);

  const load = async () => {
    if (!orgId) return;
    try {
      const r = await getPolicies({ orgId });
      setGlobal(r.global);
      setClients(r.clients);
      setBuiltIn(r.builtInHours);
      setTimeZone(r.timeZone ?? null);
      setLocale(r.locale ?? null);
      // A separate call rather than folded into getPolicies: profiles belong
      // to ticketing, and getPolicies is already the biggest payload here.
      try {
        const p = await listTicketProfiles({ orgId });
        setProfiles(p.profiles ?? []);
      } catch {
        // No profiles is a legitimate state, and failing to read them must not
        // stop the policies table rendering.
        setProfiles([]);
      }
    } catch (e) { setErr(errText(e)); }
  };
  useEffect(() => { void load(); }, [orgId]);

  if (!orgId) return null;

  async function run(key: string, fn: () => Promise<string>) {
    setBusy(key); setErr(null); setMsg(null);
    try { setMsg(await fn()); await load(); }
    catch (e) { setErr(errText(e)); }
    finally { setBusy(null); }
  }

  const shown = search.trim()
    ? clients.filter((c) => c.name.toLowerCase().includes(search.trim().toLowerCase()))
    : clients;
  const overridden = clients.filter((c) => c.policy && !isEmpty(c.policy)).length;
  const builtInT = thresholdsFor({ everyHours: builtIn });

  return (
    <div className="settings">
      <section className="panel">
        <h2 className="panel-title">Backup policies</h2>
        <p className="panel-note" style={{ marginTop: 0 }}>
          What a backup is expected to do, and therefore what counts as late. Set as little as you
          can get away with - most machines are already judged on something true about themselves.
        </p>

        {err && <div className="banner banner-error">{err}</div>}
        {msg && <div className="banner banner-ok">{msg}</div>}

        <div className="flbl" style={{ marginTop: 10 }}>Which policy applies</div>
        <table className="mini-table">
          <tbody>
            <tr><td style={{ width: 130 }}><strong>Device</strong></td>
              <td className="dim">Set on one machine. Beats everything else.</td></tr>
            <tr><td><strong>Client</strong></td>
              <td className="dim">Set for one customer. Beats the default and the evidence below.</td></tr>
            <tr><td><strong>The software</strong></td>
              <td className="dim">
                StorageCraft and ShadowControl publish each job's own schedule - its run days, its
                window and how often it repeats inside that window. Those machines are judged
                against exactly that, with no configuration from anybody.
              </td></tr>
            <tr><td><strong>Observed</strong></td>
              <td className="dim">
                Datto, MSP360 and Kaseya publish no schedule, so their machines are judged against
                the cadence they actually keep.
              </td></tr>
            <tr><td><strong>Your default</strong></td>
              <td className="dim">Used where none of the above says anything.</td></tr>
            <tr><td><strong>Built-in</strong></td>
              <td className="dim">
                Nightly, if you set no default at all - a warning after {hours(builtInT.warnAt)} and
                a failure after {hours(builtInT.failAt)}.
              </td></tr>
          </tbody>
        </table>
        <p className="panel-note">
          A machine's own behaviour beats your default on purpose: the default is a guess about
          machines nobody has looked at, and a machine backing up every 30 minutes is telling you
          exactly what it does. Anything you set by hand still beats both.
        </p>
      </section>

      <TimeZonePanel
        value={timeZone}
        locale={locale}
        disabled={!canManage}
        busy={busy === 'tz'}
        onSave={(zone, loc) => void run('tz', async () => {
          await setOrgTimeZone({ orgId: orgId!, timeZone: zone, locale: loc });
          return zone
            ? `Schedules are now read in ${zone.replace(/_/g, ' ')}.`
            : 'Time zone cleared - schedules are judged on hours elapsed.';
        })}
      />

      <section className="panel">
        <h2 className="panel-title">Your default</h2>
        <p className="panel-note" style={{ marginTop: 0 }}>
          Applied to machines whose software declares no schedule and whose own history is too
          short to read.
        </p>
        <PolicyEditor
          value={global}
          disabled={!canManage}
          busy={busy === 'global'}
          noneLabel={`use the built-in nightly rule`}
          onSave={(policy) => void run('global', async () => {
            await setGlobalPolicy({ orgId: orgId!, policy });
            return policy
              ? `Default saved: ${summarise(policy)}.`
              : 'Default cleared - the built-in rule applies.';
          })}
        />
      </section>

      <section className="panel">
        <h2 className="panel-title">
          Per client
          {overridden > 0 && <span className="tag tag-warn" style={{ marginLeft: 8 }}>{overridden} set</span>}
        </h2>
        <p className="panel-note" style={{ marginTop: 0 }}>
          Overrides your default and the machine's own history for every machine belonging to that
          client. Leave blank unless a customer genuinely differs.
        </p>

        {clients.length > 8 && (
          <input
            className="field-input" style={{ marginBottom: 8 }}
            placeholder="Find a client..." value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        )}

        {clients.length === 0 ? (
          <div className="empty">
            No clients yet. Map some under Integrations, and they will appear here.
          </div>
        ) : (
          <table className="mini-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Policy</th>
                <th style={{ width: 190 }}>Tickets</th>
                <th style={{ width: 170 }}>Ticket profile</th>
                <th style={{ width: 210 }}>Report</th>
                <th style={{ width: 130 }}></th>
              </tr>
            </thead>
            <tbody>
              {shown.map((c) => (
                <tr key={c.id}>
                  <td style={{ verticalAlign: 'top' }}>{c.name}</td>
                  <td>
                    {open === c.id ? (
                      <PolicyEditor
                        value={c.policy}
                        disabled={!canManage}
                        busy={busy === 'c' + c.id}
                        noneLabel="follow the default"
                        onSave={(policy) => void run('c' + c.id, async () => {
                          await setClientPolicy({ orgId: orgId!, clientId: c.id, policy });
                          setOpen(null);
                          return policy
                            ? `${c.name}: ${summarise(policy)}.`
                            : `${c.name} follows the default again.`;
                        })}
                      />
                    ) : (
                      <span className={c.policy && !isEmpty(c.policy) ? '' : 'dim'}>
                        {summarise(c.policy)}
                      </span>
                    )}
                  </td>
                  {/* Ticket shape has to match the CONTRACT, not the monitoring
                      tool's preference - some customers are billed per device
                      and some per site. "Follow the default" is a distinct
                      choice from picking the value the default happens to hold
                      today, because the default can change later. */}
                  <td style={{ verticalAlign: 'top' }}>
                    <select
                      className="field-input" style={{ maxWidth: 200 }}
                      disabled={!canManage || busy !== null}
                      value={c.psaGrouping ?? ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        void run('g' + c.id, async () => {
                          await setClientTicketGrouping({
                            orgId: orgId!, clientId: c.id,
                            grouping: v === 'device' || v === 'client' ? v : null,
                          });
                          return v === ''
                            ? `${c.name} follows the default ticket grouping.`
                            : `${c.name}: one ticket per ${v === 'client' ? 'client' : 'machine'}.`;
                        });
                      }}
                    >
                      <option value="">follow the default</option>
                      <option value="device">one per machine</option>
                      <option value="client">one per client</option>
                    </select>
                  </td>
                  {/* Beside the grouping it composes with. A profile sets how
                      the ticket is WRITTEN - queue, priority, type; the column
                      to the left sets how many tickets there are. */}
                  <td style={{ verticalAlign: 'top' }}>
                    <select
                      className="field-input" style={{ maxWidth: 160 }}
                      disabled={!canManage || busy !== null || profiles.length === 0}
                      value={c.ticketProfileId ?? ''}
                      onChange={(e) => {
                        const v = e.target.value || null;
                        void run('tp' + c.id, async () => {
                          await setClientTicketProfile({ orgId: orgId!, clientId: c.id, profileId: v });
                          const name = profiles.find((p) => p.id === v)?.name;
                          return v === null
                            ? `${c.name} uses the default ticket settings.`
                            : `${c.name}: ${name ?? v}.`;
                        });
                      }}
                    >
                      <option value="">
                        {profiles.length === 0 ? 'none defined' : 'default settings'}
                      </option>
                      {profiles.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </td>
                  {/* The report goes to YOUR alert recipients, not to the
                      client. Mailing a customer directly has commercial
                      consequences and is not a decision a monitoring tool makes
                      on somebody's behalf the first time a button is pressed. */}
                  <td style={{ verticalAlign: 'top' }}>
                    <select
                      className="field-input" style={{ maxWidth: 130 }}
                      disabled={!canManage || busy !== null}
                      value={c.reportCadence ?? 'off'}
                      onChange={(e) => {
                        const v = e.target.value as 'off' | 'weekly' | 'monthly';
                        void run('r' + c.id, async () => {
                          await setClientReportCadence({ orgId: orgId!, clientId: c.id, cadence: v });
                          return v === 'off'
                            ? `${c.name}: no scheduled report.`
                            : `${c.name}: a ${v} report from now on.`;
                        });
                      }}
                    >
                      <option value="off">none</option>
                      <option value="weekly">weekly</option>
                      <option value="monthly">monthly</option>
                    </select>
                  </td>
                  <td style={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                    <button className="btn-link" disabled={!canManage || busy !== null}
                            title="Build the last 30 days as a PDF and email it to your alert recipients"
                            onClick={() => void run('s' + c.id, async () => {
                              const r = await sendClientReport({ orgId: orgId!, clientId: c.id, days: 30 });
                              return `${c.name}: report sent - ${r.machines} machines, ${r.needAttention} needing attention.`;
                            })}>
                      Send now
                    </button>
                    {' '}
                    <button className="btn-link" disabled={!canManage}
                            onClick={() => setOpen(open === c.id ? null : c.id)}>
                      {open === c.id ? 'Close' : 'Edit'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <p className="panel-note">
          When a machine's actual cadence disagrees badly with the policy that applies to it, it is
          raised under <strong>Review</strong> - so a policy that has quietly stopped describing
          reality gets noticed rather than silently mis-reporting.
        </p>
      </section>
    </div>
  );
}


/**
 * The policy for ONE machine, shown inside its expanded row.
 *
 * WHY IT LIVES HERE and not on the Policies page. Damon: "how do i set a policy
 * on a machines backup. i see how to do on the general policy and the per org".
 * The callable had existed since the first cut and nothing in the UI called it,
 * so the most specific tier of the ladder - the one the whole design leans on -
 * was unreachable.
 *
 * A device policy is written at the moment somebody is looking at the machine
 * and disagreeing with its status. Putting it on a settings page would mean
 * leaving the board, finding the machine again in a list of a hundred, and
 * remembering what it was you wanted to say. The row already expands for
 * knowledge notes for exactly the same reason.
 */
export function DevicePolicyPanel({ orgId, targetId, name, policy, canManage }: {
  orgId: string | null;
  targetId: string;
  name: string;
  policy: SchedulePolicy | null | undefined;
  canManage: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [current, setCurrent] = useState<SchedulePolicy | null>(policy ?? null);
  useEffect(() => { setCurrent(policy ?? null); }, [policy]);

  if (!orgId) return null;
  const set = Boolean(current && !isEmpty(current));

  return (
    <div className="device-policy">
      <div className="device-policy-head">
        <span className="flbl">Backup policy for this machine</span>
        <span className={set ? '' : 'dim'}>{set ? summarise(current) : 'follows its client, or the default'}</span>
        {canManage && (
          <button className="btn-link" onClick={() => setOpen((o) => !o)}>
            {open ? 'Close' : set ? 'Change' : 'Set a policy'}
          </button>
        )}
      </div>

      {err && <div className="banner banner-error">{err}</div>}
      {msg && <div className="banner banner-ok">{msg}</div>}

      {open && (
        <PolicyEditor
          value={current}
          disabled={!canManage}
          busy={busy}
          noneLabel="follow this client's policy"
          onSave={(next) => {
            setBusy(true); setErr(null); setMsg(null);
            void (async () => {
              try {
                await setDevicePolicy({ orgId, targetId, policy: next });
                setCurrent(next);
                setMsg(next
                  ? `${name}: ${summarise(next)}. It will apply on the next sync.`
                  : `${name} follows its client's policy again.`);
                setOpen(false);
              } catch (e) { setErr(errText(e)); }
              finally { setBusy(false); }
            })();
          }}
        />
      )}
    </div>
  );
}
